import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Target, Trophy, ChevronRight, Play, CheckCircle2, Zap, Clock, ArrowRight, Crown, Medal, Flame } from 'lucide-react';
import { useLang } from '@/context/LangContext';
import { api } from '@/lib/api';

/**
 * Public-facing landing page for the Game IQ Simulator feature.
 *
 * Lives at `/iq-simulator-info` and is intentionally OUTSIDE the Layout
 * shell — no sidebar, no auth required. The goal is SEO + cold-traffic
 * conversion: a visitor lands here from Google, watches the demo video,
 * understands the feature in 30 seconds, and clicks Sign Up.
 *
 * Video slot: the page tries to load `/iq-demo.mp4` first; when the coach
 * uploads their real screen-recording to `/app/frontend/public/iq-demo.mp4`
 * it shows up automatically. Until then, we render an animated placeholder.
 */
const HAS_VIDEO_FILE = true; // toggle to false if you remove iq-demo.mp4

export default function IQSimulatorLanding() {
  const { t } = useLang();
  const [weekly, setWeekly] = useState(null);

  useEffect(() => {
    api.get('/iq/challenges/public/weekly-top')
      .then(r => setWeekly(r.data))
      .catch(() => setWeekly({ rows: [], window: 'weekly' }));
  }, []);

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="iq-info-page">
      {/* Top bar — minimal, public */}
      <header className="border-b border-gold/15 px-5 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-h uppercase tracking-wider">
          <img src="/icon.png" alt="PlayMaster IA" className="w-8 h-8" />
          <span className="text-gold">PlayMaster IA</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" className="px-3 py-2 text-sm text-gray-300 hover:text-white">{t('login') || 'Login'}</Link>
          <Link to="/register" className="px-4 py-2 rounded-md bg-gold text-navy text-sm font-h uppercase tracking-wider hover:bg-gold-light">
            {t('signup') || 'Sign Up'}
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-5 py-12 lg:py-16">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-4 inline-flex items-center gap-2">
          <Brain className="w-4 h-4" /> {t('iq_info_eyebrow') || 'New · Premium Annual feature'}
        </div>
        <h1 className="font-h uppercase tracking-tight text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] mb-4">
          <span className="block text-white">{t('iq_info_h1_a') || 'Train basketball'}</span>
          <span className="block gold-text-gradient">{t('iq_info_h1_b') || 'IQ like a pro'}</span>
        </h1>
        <p className="text-xl text-gold/90 max-w-3xl mb-4 leading-snug">
          {t('iq_info_subtitle') || 'The world\'s first AI Basketball IQ Simulator — train real-game decisions, not isolated drills.'}
        </p>
        <p className="text-base text-gray-300 max-w-3xl mb-8 leading-relaxed">
          {t('iq_info_para') || 'Game IQ Simulator presents real basketball scenarios — pick-and-roll coverages, last-possession reads, transition defense. Pick the smartest decision, get instant AI coaching feedback, and watch your IQ score grow. Built for youth, high school, AAU, academies and pro-level players.'}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light transition"
            data-testid="iq-info-cta-signup"
          >
            {t('iq_info_cta_primary') || 'Start training your IQ'} <ChevronRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border-2 border-gold/40 text-gold font-h uppercase tracking-wider hover:bg-gold/10 transition"
            data-testid="iq-info-cta-pricing"
          >
            {t('iq_info_cta_pricing') || 'See plans'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* VIDEO DEMO SLOT */}
      <section className="max-w-5xl mx-auto px-5 pb-12">
        <div className="relative rounded-2xl overflow-hidden border-2 border-gold/40 bg-navy-deep shadow-2xl aspect-video">
          {HAS_VIDEO_FILE ? (
            <video
              src="/iq-demo.mp4"
              controls
              playsInline
              preload="metadata"
              poster="/iq-demo-poster.jpg"
              className="w-full h-full object-cover"
              data-testid="iq-demo-video"
            >
              {t('iq_info_video_fallback') || 'Your browser does not support inline video. Sign up to try the simulator live.'}
            </video>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-navy-deep to-navy">
              <div className="w-20 h-20 rounded-full bg-gold/15 border-2 border-gold flex items-center justify-center">
                <Play className="w-9 h-9 text-gold ml-1" />
              </div>
              <div className="text-gold font-h uppercase tracking-widest text-sm">
                {t('iq_info_video_coming') || 'Live demo video coming soon'}
              </div>
              <Link to="/register" className="text-xs text-gray-400 hover:text-gold underline">
                {t('iq_info_try_live') || 'Try it live — sign up free'}
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-6xl mx-auto px-5 py-12">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-3">
          {t('iq_info_pillars_eyebrow') || 'Why Game IQ matters'}
        </div>
        <h2 className="font-h uppercase text-3xl sm:text-4xl mb-3 leading-tight">
          {t('iq_info_pillars_h2') || 'Stop drilling. Start deciding.'}
        </h2>
        <p className="text-gray-300 max-w-3xl mb-10">
          {t('iq_info_pillars_desc') || 'Elite players are made of decisions. PlayMaster IA brings the decision-making rep — the rep that scouting reports call "basketball IQ" — into a structured, repeatable training format.'}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Pillar icon={Brain} title={t('iq_info_p1_t') || 'Real-game scenarios'} desc={t('iq_info_p1_d') || 'Pick-and-roll vs Drop / Hedge / Switch · Zone offense · Last possession · Transition · Star ISO defense — situations every competitive player faces by U14.'} />
          <Pillar icon={Target} title={t('iq_info_p2_t') || 'Instant AI coaching'} desc={t('iq_info_p2_d') || 'Each decision is evaluated by Claude AI — you get specific feedback in your language, not generic praise. Read the explanation, fix the mistake.'} />
          <Pillar icon={Trophy} title={t('iq_info_p3_t') || 'Track your IQ Score'} desc={t('iq_info_p3_d') || 'Build a measurable score across categories. Climb the rank ladder from Walk-on to Floor General. Coaches see player progress in their dashboard.'} />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-5 py-12 border-t border-gold/15">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-3">
          {t('iq_info_how_eyebrow') || 'How it works'}
        </div>
        <h2 className="font-h uppercase text-3xl sm:text-4xl mb-8">
          {t('iq_info_how_h2') || '3 steps to a smarter player'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Step n="01" icon={Play} title={t('iq_info_s1_t') || 'Pick a scenario'} desc={t('iq_info_s1_d') || 'Choose Offensive, Defensive — soon Complete Mode with score, clock and fouls.'} />
          <Step n="02" icon={Clock} title={t('iq_info_s2_t') || 'Decide in seconds'} desc={t('iq_info_s2_d') || 'Read the court, pick the best option from 3 choices. Reaction time counts.'} />
          <Step n="03" icon={Zap} title={t('iq_info_s3_t') || 'Get AI feedback'} desc={t('iq_info_s3_d') || 'Claude AI explains why your read worked — or what the optimal call was.'} />
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="max-w-6xl mx-auto px-5 py-12 border-t border-gold/15">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-3">
              {t('iq_info_who_eyebrow') || 'Built for'}
            </div>
            <h2 className="font-h uppercase text-3xl sm:text-4xl mb-4">
              {t('iq_info_who_h2') || 'Coaches, players and academies that take IQ seriously'}
            </h2>
            <ul className="space-y-2 text-gray-200">
              {(t('iq_info_who_list') || 'Youth basketball coaches | High school programs | AAU clubs | Basketball academies | Pro & semi-pro player development | Individual players age 12+').split('|').map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span>{item.trim()}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border-2 border-gold/40 bg-card-steel p-6">
            <div className="text-xs font-mono uppercase tracking-widest text-gold mb-2">
              {t('iq_info_inside_title') || 'Inside the Annual Premium plan'}
            </div>
            <div className="font-h text-4xl mb-1">$249.99<span className="text-base text-gray-400 font-normal">/{t('year') || 'year'}</span></div>
            <p className="text-sm text-gray-300 mb-4">
              {t('iq_info_inside_desc') || 'Game IQ Simulator is included in the Annual Premium plan — together with the full playbook, AI Coach, multi-team management, academy mode and priority support.'}
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm hover:bg-gold-light"
              data-testid="iq-info-cta-pricing-card"
            >
              {t('iq_info_cta_pricing') || 'See plans'} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-gold/15 bg-navy-deep">
        <div className="max-w-4xl mx-auto px-5 py-16 text-center">
          <h2 className="font-h uppercase text-4xl sm:text-5xl mb-4">
            {t('iq_info_final_h2') || 'Make your next basketball decision a smart one'}
          </h2>
          <p className="text-gray-300 mb-8">
            {t('iq_info_final_para') || 'Join the coaches and players already training their basketball IQ with PlayMaster IA.'}
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-bold hover:bg-gold-light transition"
          >
            {t('iq_info_cta_primary') || 'Start training your IQ'} <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* WEEKLY PUBLIC LEADERBOARD — social proof + share-able links */}
      <WeeklyTop weekly={weekly} t={t} />

      <footer className="border-t border-gold/15 px-5 py-6 text-center text-xs text-gray-500">
        <Link to="/" className="text-gold hover:underline">← {t('back_home') || 'Back to home'}</Link>
      </footer>
    </div>
  );
}

function WeeklyTop({ weekly, t }) {
  if (!weekly || !weekly.rows || weekly.rows.length === 0) return null;
  const isWeekly = weekly.window === 'weekly';
  return (
    <section
      className="border-t border-gold/15 bg-gradient-to-b from-navy to-navy-deep"
      data-testid="iq-info-weekly-top"
    >
      <div className="max-w-4xl mx-auto px-5 py-16">
        <div className="flex items-center gap-2 mb-2">
          <Flame className="w-5 h-5 text-gold" />
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold">
            {isWeekly
              ? (t('iq_info_weekly_eyebrow') || 'Hot this week')
              : (t('iq_info_alltime_eyebrow') || 'Most played all-time')}
          </div>
        </div>
        <h2 className="font-h uppercase text-3xl sm:text-4xl mb-2 text-white">
          {t('iq_info_weekly_h') || 'Top Challenges'}
        </h2>
        <p className="text-gray-400 text-sm mb-6 max-w-2xl">
          {t('iq_info_weekly_sub') || 'Live ranking of the most-played coach-made challenges. Click any code to take it — no signup needed to preview.'}
        </p>

        <div className="bg-card-steel border-2 border-gold/30 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 px-4 py-2 border-b border-gold/20">
            <div className="col-span-1">#</div>
            <div className="col-span-2">{t('iq_info_weekly_code') || 'Code'}</div>
            <div className="col-span-6">{t('iq_info_weekly_title') || 'Challenge'}</div>
            <div className="col-span-3 text-right">{isWeekly ? (t('iq_info_weekly_runs') || 'This week') : (t('iq_info_weekly_plays') || 'Plays')}</div>
          </div>
          {weekly.rows.map(row => {
            const RankIcon = row.rank === 1 ? Crown : row.rank <= 3 ? Medal : null;
            return (
              <Link
                key={row.code}
                to={`/iq-challenge/${row.code}`}
                data-testid={`iq-info-top-row-${row.code}`}
                className="grid grid-cols-12 items-center px-4 py-3 border-b border-gold/10 last:border-b-0 text-sm hover:bg-gold/5 transition"
              >
                <div className={`col-span-1 font-h ${row.rank <= 3 ? 'text-gold' : 'text-gray-400'} flex items-center gap-1`}>
                  {RankIcon && <RankIcon className="w-3.5 h-3.5" />}{row.rank}
                </div>
                <div className="col-span-2 font-mono text-gold text-xs tracking-widest">{row.code}</div>
                <div className="col-span-6 min-w-0">
                  <div className="font-h uppercase text-xs tracking-wider text-white truncate">{row.title}</div>
                  <div className="text-[10px] font-mono text-gray-500 uppercase">{row.creator_name}</div>
                </div>
                <div className="col-span-3 text-right font-h text-white">
                  {isWeekly ? row.weekly_attempts : row.total_attempts}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Pillar({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-lg border-2 border-gold/25 bg-card-steel p-5 hover:border-gold transition">
      <Icon className="w-7 h-7 text-gold mb-3" />
      <div className="font-h uppercase text-lg mb-2 text-white">{title}</div>
      <p className="text-sm text-gray-300 leading-relaxed">{desc}</p>
    </div>
  );
}

function Step({ n, icon: Icon, title, desc }) {
  return (
    <div className="flex gap-4 items-start">
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/15 border-2 border-gold flex items-center justify-center">
        <Icon className="w-5 h-5 text-gold" />
      </div>
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-gold/70 mb-1">{n}</div>
        <div className="font-h uppercase text-lg text-white mb-1">{title}</div>
        <p className="text-sm text-gray-300">{desc}</p>
      </div>
    </div>
  );
}
